import { dateToApiFormat, formatDate } from '@/helpers/dateHelper';
import { useAvailabilityExceptionRequests } from '@/services/availability/useAvailabilityExceptionRequests';
import { ApprovalState } from '@/services/gql/graphql.generated';
import { useScheduleRequestStatus } from '@/services/workingPatterns/useScheduleRequestStatus';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useDismissedScheduleAlerts } from './useDismissedScheduleAlerts';

export type ScheduleAlert = {
  key: string;
  timestamp: number;
  message: string;
  status: ApprovalState;
  onView: () => void;
  onDismiss?: () => void;
};

const toTime = (value?: string | null) => (value ? new Date(value).getTime() : 0);

const leaveDateRange = (request: { start: string; end: string }) =>
  `${formatDate(request.start)} to ${formatDate(request.end)}`;

export const useScheduleAlerts = (employeeId?: string) => {
  const navigate = useNavigate();
  const {
    pendingRequest: pendingRequestHistory,
    upcomingApprovedRequests,
    upcomingRejectedRequests,
    hasPendingRequest
  } = useScheduleRequestStatus(employeeId);
  const { requests: availabilityExceptionRequests } = useAvailabilityExceptionRequests(employeeId);
  const { isDismissed, dismiss } = useDismissedScheduleAlerts(employeeId);

  const today = dateToApiFormat(new Date());

  const leaveRequests = availabilityExceptionRequests.filter(request =>
    request.exceptions?.some(exception => !!exception?.leaveType)
  );

  // The approved schedule alert is an aggregate, so its dismissal is keyed by the set of approved
  // request IDs. A newly approved request changes the key and produces a fresh, undismissed alert.
  const approvedDismissKey = `schedule-approved:${upcomingApprovedRequests
    .map(r => r.id)
    .sort()
    .join(',')}`;

  const pendingDismissKey = pendingRequestHistory
    ? `${pendingRequestHistory.id}:${ApprovalState.Requested}`
    : undefined;

  const alerts: ScheduleAlert[] = [
    // Rejected schedule change requests
    ...upcomingRejectedRequests
      .filter(r => r.id && !isDismissed(`${r.id}:${ApprovalState.Rejected}`))
      .map<ScheduleAlert>(r => {
        const dismissKey = `${r.id}:${ApprovalState.Rejected}`;
        return {
          key: `schedule-rejected-${dismissKey}`,
          timestamp: toTime(r.updatedOn),
          message: `Your schedule change request, effective from ${format(new Date(r.effectiveFrom), 'do MMMM yyyy')}, has been denied${r.rejectionReason ? `: ${r.rejectionReason}` : ''}`,
          status: ApprovalState.Rejected,
          onView: () => navigate(`/ess/my-schedule/change-request/${r.id}`),
          onDismiss: () => dismiss(dismissKey)
        };
      }),

    // Leave requests (rejected, cancelled by manager, upcoming approved)
    ...leaveRequests.flatMap<ScheduleAlert>(request => {
      const normalisedState =
        request.requestState === ApprovalState.Applied ? ApprovalState.Approved : request.requestState;
      const dismissKey = `${request.id}:${normalisedState}`;

      if (isDismissed(dismissKey)) return [];

      const base = {
        key: `leave-${dismissKey}`,
        timestamp: toTime(request.updatedOn),
        onView: () => navigate(`/ess/my-calendar/my-leave/${request.id}`),
        onDismiss: () => dismiss(dismissKey)
      };

      switch (request.requestState) {
        case ApprovalState.Rejected:
          return [
            {
              ...base,
              message: `Your leave request, ${leaveDateRange(request)}, was denied${request.rejectionReason ? `: ${request.rejectionReason}` : ''}`,
              status: ApprovalState.Rejected
            }
          ];
        case ApprovalState.CancelledByManager:
          return [
            {
              ...base,
              message: `Your leave request, ${leaveDateRange(request)}, was cancelled by your manager${request.rejectionReason ? `: ${request.rejectionReason}` : ''}`,
              status: ApprovalState.CancelledByManager
            }
          ];
        case ApprovalState.Approved:
        case ApprovalState.Applied:
          if (request.end < today) return [];
          return [
            {
              ...base,
              message: `Your leave request, ${leaveDateRange(request)}, has been approved.`,
              status: ApprovalState.Approved
            }
          ];
        default:
          return [];
      }
    }),

    // Pending schedule change
    ...(hasPendingRequest && pendingRequestHistory && pendingDismissKey && !isDismissed(pendingDismissKey)
      ? [
          {
            key: `schedule-pending-${pendingDismissKey}`,
            timestamp: toTime(pendingRequestHistory.updatedOn),
            message: 'You have a pending schedule change request.',
            status: ApprovalState.Requested,
            onView: () => navigate(`/ess/my-schedule/change-request/${pendingRequestHistory.id}`),
            onDismiss: () => dismiss(pendingDismissKey)
          } satisfies ScheduleAlert
        ]
      : []),

    // Approved schedule changes with upcoming effective dates
    ...(upcomingApprovedRequests.length > 0 && !isDismissed(approvedDismissKey)
      ? [
          {
            key: `schedule-approved-${approvedDismissKey}`,
            timestamp: Math.max(...upcomingApprovedRequests.map(r => toTime(r.updatedOn))),
            message:
              upcomingApprovedRequests.length === 1
                ? `Your schedule change has been approved and will take effect from ${upcomingApprovedRequests[0].effectiveFrom}.`
                : `${upcomingApprovedRequests.length} schedule updates approved with upcoming effective dates.`,
            status: ApprovalState.Approved,
            onView: () =>
              upcomingApprovedRequests.length > 1
                ? navigate(`/ess/my-schedule/change-requests`)
                : navigate(`/ess/my-schedule/change-request/${upcomingApprovedRequests[0].id}`),
            onDismiss: () => dismiss(approvedDismissKey)
          } satisfies ScheduleAlert
        ]
      : [])
  ];

  const latestAlert = [...alerts].sort((a, b) => b.timestamp - a.timestamp)[0];

  return { latestAlert, hasPendingRequest };
};
